import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { authService } from "@/services/authService";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, ArrowLeft, Eye, EyeOff, Phone } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useSafeBack } from "@/lib/back";
import { useAuth } from "@/hooks/useAuth";

// Évaluation de la force du mot de passe (pure function)
type PasswordStrength = { score: 0 | 1 | 2 | 3; label: string; color: string };

const evaluatePasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
    if (/\d/.test(password) && /[^A-Za-z0-9]/.test(password)) score++;

    const levels: PasswordStrength[] = [
        { score: 0, label: "Très faible", color: "bg-destructive" },
        { score: 1, label: "Faible", color: "bg-destructive" },
        { score: 2, label: "Moyen", color: "bg-yellow-500" },
        { score: 3, label: "Fort", color: "bg-green-500" },
    ];
    return levels[score];
};

const normalizePhone = (value: string): string => value.replace(/\D/g, "");

const formSchema = z.object({
    email: z.string().email("Email invalide"),
    password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
    firstName: z.string().nonempty("Veuillez entrer un prénom"),
    lastName: z.string().nonempty("Veuillez entrer un nom"),
    confirmPassword: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
    phoneNumber: z
        .string()
        .regex(/^\d{10,15}$/, "Le numéro doit être au format international, ex: 22670000000"),
    acceptTerms: z.boolean().refine((v) => v === true, {
        message: "Vous devez accepter les conditions d'utilisation",
    }),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
});

type FormValues = z.infer<typeof formSchema>;

function RegisterPage() {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const goBack = useSafeBack("/");
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const firstNameRef = useRef<HTMLInputElement>(null);
    const { updateUser } = useAuth();

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
            firstName: "",
            lastName: "",
            password: "",
            confirmPassword: "",
            phoneNumber: "",
            acceptTerms: false,
        },
    });

    // Auto-focus sur le champ prénom au montage
    useEffect(() => {
        firstNameRef.current?.focus();
    }, []);

    // Surveille la force du mot de passe
    const passwordValue = form.watch("password");
    const strength = evaluatePasswordStrength(passwordValue || "");

    const onSubmit = async (data: FormValues) => {
        setIsLoading(true);
        setError(null);
        try {
            const cleanPhone = normalizePhone(data.phoneNumber);

            const registerResponse = await authService.register({
                email: data.email,
                password: data.password,
                firstName: data.firstName,
                lastName: data.lastName,
                phoneNumber: cleanPhone,
            });

            if (!registerResponse?.success) {
                setError(
                    registerResponse?.error?.message ||
                    "Une erreur est survenue lors de l'inscription"
                );
                return;
            }

            updateUser({ phone_number: cleanPhone });

            toast.success("Un email de vérification vous a été envoyé. Merci de le consulter pour poursuivre votre inscription");

            if (registerResponse.redirectTo !== undefined) {
                navigate(registerResponse.redirectTo, { replace: true });
            } else {
                navigate("/parrainage", { replace: true });
            }
        } catch (err: unknown) {
            if (err instanceof Error) {
                console.error("Erreur :", err.message);
                setError(err.message);
            } else {
                console.error("Erreur inconnue :", err);
                setError("Une erreur inconnue est survenue");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]">
            <div
                aria-hidden
                className="pointer-events-none fixed inset-0 -z-10"
                style={{
                    background:
                        "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
                }}
            />

            <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
                <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
                    <button
                        type="button"
                        onClick={goBack}
                        aria-label="Retour"
                        className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring"
                    >
                        <ArrowLeft className="size-4" />
                    </button>
                    <h1 className="text-base font-semibold tracking-tight">Inscription</h1>
                </div>
            </header>

            <main className="mx-auto w-full max-w-[52ch] flex-1 px-4 py-6 lg:max-w-6xl lg:px-8">
                <div className="mx-auto w-full max-w-sm">
                    <div className="mb-6 text-center">
                        <h2 className="text-2xl font-semibold tracking-tight">Créer un compte</h2>
                        <p className="mt-1 text-sm text-muted-foreground">Entrez vos informations pour vous inscrire</p>
                    </div>

                    {error && (
                        <div
                            role="alert"
                            aria-live="polite"
                            className="mb-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3.5 text-sm text-destructive"
                        >
                            <p className="flex items-start gap-2 font-semibold">
                                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                                Échec d'authentification
                            </p>
                            <p className="mt-1 opacity-90">{error}</p>
                        </div>
                    )}

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="firstName"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label htmlFor="firstName" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Prénom(s)</Label>
                                        <FormControl>
                                            <Input
                                                id="firstName"
                                                type="text"
                                                required
                                                autoComplete="given-name"
                                                className="h-auto w-full rounded-xl bg-surface px-4 py-3 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20"
                                                {...field}
                                                ref={(el) => {
                                                    field.ref(el);
                                                    firstNameRef.current = el;
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="lastName"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label htmlFor="lastName" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Nom</Label>
                                        <FormControl>
                                            <Input id="lastName" type="text" required autoComplete="family-name" className="h-auto w-full rounded-xl bg-surface px-4 py-3 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label htmlFor="email" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Email</Label>
                                        <FormControl>
                                            <Input id="email" type="email" placeholder="m@example.com" required autoComplete="email" className="h-auto w-full rounded-xl bg-surface px-4 py-3 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="phoneNumber"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label htmlFor="phoneNumber" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Numéro de téléphone (format international)</Label>
                                        <FormControl>
                                            <div className="relative">
                                                <div className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg bg-muted p-2">
                                                    <Phone className="size-4 text-muted-foreground" />
                                                </div>
                                                <Input
                                                    id="phoneNumber"
                                                    type="tel"
                                                    inputMode="tel"
                                                    placeholder="22670000000"
                                                    required
                                                    autoComplete="tel"
                                                    className="h-auto w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20"
                                                    {...field}
                                                    onChange={(e) => field.onChange(normalizePhone(e.target.value))}
                                                />
                                            </div>
                                        </FormControl>
                                        <p className="mt-1 text-[11px] text-muted-foreground">Ex : 22670000000 (indicatif + numéro sans le +)</p>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label htmlFor="password" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Mot de passe</Label>
                                        <FormControl>
                                            <div className="relative">
                                                <Input
                                                    id="password"
                                                    type={showPassword ? "text" : "password"}
                                                    required
                                                    autoComplete="new-password"
                                                    className="w-full rounded-xl bg-surface px-4 py-3 pr-10 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20"
                                                    {...field}
                                                />
                                                <button
                                                    type="button"
                                                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-subtle hover:text-foreground"
                                                    onClick={() => setShowPassword((v) => !v)}
                                                >
                                                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                                </button>
                                            </div>
                                        </FormControl>
                                        {passwordValue && (
                                            <div className="mt-2 flex items-center gap-2">
                                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                                                    <div
                                                        className={`h-full transition-all ${strength.color}`}
                                                        style={{ width: `${(strength.score / 3) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="text-[10px] font-medium text-muted-foreground">{strength.label}</span>
                                            </div>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="confirmPassword"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label htmlFor="confirmPassword" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Confirmer le mot de passe</Label>
                                        <FormControl>
                                            <div className="relative">
                                                <Input
                                                    id="confirmPassword"
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    required
                                                    autoComplete="new-password"
                                                    className="w-full rounded-xl bg-surface px-4 py-3 pr-10 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20"
                                                    {...field}
                                                />
                                                <button
                                                    type="button"
                                                    aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-subtle hover:text-foreground"
                                                    onClick={() => setShowConfirmPassword((v) => !v)}
                                                >
                                                    {showConfirmPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                                </button>
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="acceptTerms"
                                render={({ field }) => (
                                    <FormItem className="flex items-start gap-2">
                                        <FormControl>
                                            <input
                                                id="acceptTerms"
                                                type="checkbox"
                                                className="mt-0.5 size-4 rounded border-border accent-foreground"
                                                checked={!!field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        </FormControl>
                                        <Label htmlFor="acceptTerms" className="text-xs font-medium text-muted-foreground">
                                            J'accepte les{" "}
                                            <Link to="/terms" className="font-semibold text-foreground underline underline-offset-2">
                                                conditions d'utilisation
                                            </Link>{" "}
                                            et la{" "}
                                            <Link to="/privacy" className="font-semibold text-foreground underline underline-offset-2">
                                                politique de confidentialité
                                            </Link>
                                        </Label>
                                    </FormItem>
                                )}
                            />
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3 text-sm font-semibold text-background shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:opacity-90 enabled:active:scale-[0.99]"
                            >
                                {isLoading ? <Loader2 className="size-4 animate-spin" /> : "S'inscrire"}
                            </button>
                        </form>
                    </Form>

                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        Vous avez déjà un compte ?{" "}
                        <Link
                            to="/login"
                            replace
                            className="font-semibold text-foreground underline underline-offset-4 hover:text-foreground"
                        >
                            Se connecter
                        </Link>
                    </p>
                </div>
            </main>
        </div>
    );
}

export default RegisterPage;